import { BsCheck } from "react-icons/all";
import { Flex, useColorModeValue, VStack } from "@chakra-ui/react";
import { useHistory } from "react-router-dom";

import { PasswordStrengthBar } from "modules/authentication/presentation/PasswordStrengthBar";
import { useResetPassword } from "modules/authentication/infrastructure/useResetPassword";
import { IResetPasswordForm } from "modules/authentication/application/types/IResetPasswordForm";
import { useResetPasswordNotification } from "modules/authentication/presentation/useResetPasswordNotification";
import { resetPasswordFormValidationSchema } from "modules/authentication/application/validation/resetPasswordFormValidationSchema";

import { useGetFriendlyUrl } from "utils/hooks/useGetFriendlyUrl";

import {
  Form,
  IErrorField,
  SpinnerButton,
  TextField,
  TextFieldType,
} from "components/Form";
import { ChakraText } from "components/ChakraText";
import { Icon } from "components/Icon";
import { useGetThemeColors } from "ChakraTheme";

interface IProps {
  tokenId: string;
}

const ResetPasswordForm = ({ tokenId }: IProps) => {
  const colors = useGetThemeColors();
  const iconColor = useColorModeValue(colors.white, colors.black);
  const { friendlyUrl } = useGetFriendlyUrl();
  const history = useHistory();
  const mutation = useResetPassword(tokenId);
  const {
    showSuccessNotification,
    showErrorNotification,
  } = useResetPasswordNotification();

  return (
    <Form<IResetPasswordForm>
      onSubmit={async (
        model: IResetPasswordForm,
        setCustomErrors: (errors: IErrorField[]) => void
      ) => {
        const formData = new FormData();
        Object.keys(model).forEach((key) => {
          formData.append(key, model[key as keyof typeof model].toString());
        });
        try {
          await mutation.mutateAsync(formData);
          history.push(`/e-rezerwacja/${friendlyUrl}`);
          showSuccessNotification();
        } catch (e) {
          if (e.status === 400) {
            setCustomErrors(e.response.msg);
          }
          showErrorNotification(e.message);
        }
      }}
      defaultValues={{
        password: "",
        repeatPassword: "",
      }}
      validationSchema={resetPasswordFormValidationSchema}
      resetOnSubmit={false}
    >
      {({ watch }) => {
        const password = watch("password");
        return (
          <VStack spacing={4} align="stretch">
            <TextField
              label="Hasło"
              name="password"
              type={TextFieldType.PASSWORD}
            />
            <TextField
              label="Powtórz hasło"
              name="repeatPassword"
              type={TextFieldType.PASSWORD}
            />
            <PasswordStrengthBar password={password} />
            <Flex mt={6} justifyContent="center" pt={4}>
              <SpinnerButton
                id="confirm-reset-password-btn"
                btnTitle={<ChakraText>Zapisz</ChakraText>}
                leftIcon={<Icon color={iconColor} icon={BsCheck} />}
                w="250px"
                h="60px"
              />
            </Flex>
          </VStack>
        );
      }}
    </Form>
  );
};

export { ResetPasswordForm };
